import { useState } from "react";
import { Phone, MapPin, HeartHandshake, ShieldCheck, Baby, User, Laptop, Navigation } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SectionTitle from "@/components/SectionTitle";
import MapView from "@/components/MapView";
import SEO from "@/components/SEO";

const RESOURCES = [
  { icon: Phone, t: "Global Emergency Numbers", d: "112 (EU), 911 (US), 999 (UK), 000 (AU) — local police & ambulance." },
  { icon: HeartHandshake, t: "UN Trafficking Hotline", d: "National Human Trafficking Hotline: 1-888-373-7888 (US, 24/7)." },
  { icon: User, t: "Women's Support", d: "UN Women Safe Cities Global · Local shelters & counselling directories." },
  { icon: Baby, t: "Child Protection", d: "Childline International · UNICEF Child Helpline Alliance." },
  { icon: Laptop, t: "Cybercrime Help", d: "IC3 (US), Action Fraud (UK), Europol Cybercrime Centre reporting." },
  { icon: ShieldCheck, t: "Legal Aid", d: "IBA · Pro-bono directories · National bar associations." },
];

const SUPPORT_POINTS = [
  { name: "UNODC Vienna", lat: 48.235, lng: 16.417, detail: "UN Office on Drugs and Crime HQ" },
  { name: "INTERPOL Lyon", lat: 45.784, lng: 4.847, detail: "General Secretariat" },
  { name: "Europol The Hague", lat: 52.09, lng: 4.29, detail: "European law enforcement agency" },
  { name: "IC3 (FBI) USA", lat: 38.9, lng: -77.03, detail: "Internet Crime Complaint Center" },
  { name: "Action Fraud UK", lat: 51.507, lng: -0.128, detail: "National fraud reporting" },
  { name: "AFP Australia", lat: -35.28, lng: 149.13, detail: "Australian Federal Police" },
  { name: "Childline India", lat: 19.076, lng: 72.877, detail: "24/7 child helpline · 1098" },
  { name: "IOM Geneva", lat: 46.204, lng: 6.143, detail: "International Organization for Migration" },
];

export default function VictimSupport() {
  const [loc, setLoc] = useState("");
  const [points, setPoints] = useState(SUPPORT_POINTS);
  const [origin, setOrigin] = useState(null);
  const [busy, setBusy] = useState(false);

  const runSearch = async (params) => {
    setBusy(true);
    try {
      const r = await api.get("/support/near", { params });
      setPoints(r.data.points || SUPPORT_POINTS);
      setOrigin(r.data.origin || null);
      if (r.data.origin) toast.success(`Sorted by distance from ${r.data.origin.label.slice(0, 60)}`);
      else if (params.q) toast.error("Location not found. Please try again.");
    } catch {
      toast.error("Search failed.");
    } finally { setBusy(false); }
  };

  const detect = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported.");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => runSearch({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => { setBusy(false); toast.error("Location permission denied."); },
      { timeout: 8000 }
    );
  };

  const submitSearch = (e) => { e.preventDefault(); if (loc.trim()) runSearch({ q: loc.trim() }); };

  return (
    <div>
      {/* Emergency banner */}
      <div className="bg-red-950/40 border-y border-red-800/30" data-testid="emergency-banner">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <div className="text-red-300 font-mono text-xs tracking-widest">EMERGENCY</div>
            <div className="text-white font-display text-xl">If you are in immediate danger, call your local emergency number now.</div>
          </div>
          <Button className="rounded-full bg-red-500 hover:bg-red-400 text-white h-11 px-6 animate-pulse-gold" data-testid="help-now-btn">HELP NOW</Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="victim-support-page">
        <SEO title="Victim Support Centre" description="You are not alone. Global emergency contacts, trafficking hotlines, cybercrime reporting, women's and child protection resources — geolocated." path="/victim-support" image="justice" />
        <SectionTitle eyebrow="VICTIM SUPPORT CENTRE" title="You are not alone. Help is here." subtitle="Search resources by location, or explore support categories below." />

        <form onSubmit={submitSearch} className="mt-8 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
            <Input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="City, country or address" data-testid="support-search-input" className="pl-9 bg-black border-white/10 h-11 rounded-full text-white" />
          </div>
          <Button type="submit" disabled={busy} className="rounded-full bg-gold text-black hover:bg-gold-light h-11 px-6" data-testid="support-search-btn">{busy ? "Searching…" : "Find Resources"}</Button>
          <Button type="button" onClick={detect} disabled={busy} variant="outline" className="rounded-full h-11 px-6 border-gold/50 text-gold hover:bg-gold/10 bg-transparent" data-testid="support-detect-btn">
            <Navigation className="mr-2 h-4 w-4" /> Use my location
          </Button>
        </form>

        {/* Interactive map with support resource pins */}
        <div className="mt-6" data-testid="support-map">
          <MapView
            points={points}
            height={340}
            center={origin ? [origin.lat, origin.lng] : [30, 10]}
            zoom={origin ? 4 : 2}
          />
        </div>

        {origin && (
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="support-near-results">
            {points.slice(0, 6).map((p, i) => (
              <div key={i} className="rounded-xl border border-white/5 bg-obsidian-soft p-4">
                <div className="flex items-center justify-between">
                  <div className="font-display text-lg">{p.name}</div>
                  {p.distance_km !== undefined && <span className="text-xs font-mono text-gold">{p.distance_km.toLocaleString()} km</span>}
                </div>
                <div className="text-white/50 text-xs mt-1">{p.detail}</div>
                <div className="text-white/40 text-[10px] mt-1 font-mono">{p.country}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {RESOURCES.map((r, i) => (
            <Card key={i} className="bg-obsidian-soft border-white/5 rounded-2xl" data-testid={`support-card-${i}`}>
              <CardContent className="p-6">
                <div className="h-11 w-11 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center"><r.icon className="h-5 w-5 text-gold" /></div>
                <div className="font-display text-xl mt-5">{r.t}</div>
                <div className="mt-2 text-white/60 text-sm">{r.d}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
